import { API_BASE, isCrossOriginApi } from './api';

/**
 * Reports a page view or a sign-in to the pack's own analytics endpoint.
 *
 * Only the event kind and the path are sent. Who it was is resolved server-side from the Static Web
 * Apps principal, so this cannot attribute a visit to the wrong person and an anonymous visitor is
 * simply not recorded.
 */

export type TrackedEvent = 'pageview' | 'signin';

const TRACK_URL = `${API_BASE}/track`;

// Set when a sign-in button is clicked, read on the page the visitor returns to. Only the nav link
// goes through /login-success; the buttons on the newsletter and photo pages send the visitor straight
// back to that page, so without this their sign-in would be recorded only as a page view.
const PENDING_SIGNIN_KEY = 'pack1259.pendingSignIn';

export function markSignInPending(): void {
  try {
    window.sessionStorage.setItem(PENDING_SIGNIN_KEY, '1');
  } catch {
    // Storage blocked: the sign-in still works, it just goes uncounted.
  }
}

/** Reports the sign-in a button started, once. A cancelled sign-in arrives anonymous and is discarded. */
export function trackPendingSignIn(path: string): void {
  try {
    if (!window.sessionStorage.getItem(PENDING_SIGNIN_KEY)) return;
    window.sessionStorage.removeItem(PENDING_SIGNIN_KEY);
  } catch {
    return;
  }
  track('signin', path);
}

export function track(event: TrackedEvent, path: string, referrer?: string): void {
  if (typeof window === 'undefined') return;

  const body = JSON.stringify({ event, path, referrer });

  try {
    // sendBeacon survives the navigation that triggered it, which a normal fetch does not - and as
    // a text/plain body it is a simple request, so it needs no CORS preflight. It only carries the
    // session cookie same-origin, so a cross-origin API has to use fetch instead or every event
    // would arrive without an identity and be discarded.
    if (!isCrossOriginApi && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const sent = navigator.sendBeacon(TRACK_URL, new Blob([body], { type: 'text/plain' }));
      if (sent) return;
    }

    fetch(TRACK_URL, {
      method: 'POST',
      body,
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'text/plain' },
    }).catch(() => {
      // Analytics must never surface to a reader. A dropped page view is not worth a console error.
    });
  } catch {
    // As above - including the case where a browser extension has removed sendBeacon.
  }
}
